import { BotonExportar } from '@/components/dominio/BotonExportar';
import { ComentarioSeccion } from '@/components/dominio/ComentarioSeccion';
import { KpiCard } from '@/components/dominio/KpiCard';
import { MontoCRC } from '@/components/dominio/MontoCRC';
import { SelectorPeriodo } from '@/components/dominio/SelectorPeriodo';
import { SortableHeader, useSortColumn } from '@/components/dominio/SortableHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePeriodo } from '@/hooks/usePeriodo';
import { useIndirectos } from '@/hooks/useUtilidades';
import { formatearPeriodo, mesAnclaPeriodo } from '@adelante/shared';
import { Loader2 } from 'lucide-react';
import { useMemo, useRef } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const MESES_CORTOS = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

function abreviarCRC(valor: number): string {
  if (Math.abs(valor) >= 1e9) return `₡${(valor / 1e9).toFixed(1)}bn`;
  if (Math.abs(valor) >= 1e6) return `₡${(valor / 1e6).toFixed(0)}M`;
  return `₡${valor.toLocaleString('es-CR')}`;
}

function formatearPct(v: number | null): string {
  return v == null ? '—' : `${v.toFixed(2)}%`;
}

type FilaLote = { lote: string; indirecto_total: number; cantidad_movimientos: number };

export function IndirectosPantalla() {
  const { periodo, setModo, setAnio, setMes, setTrimestre, setDesde, setHasta } = usePeriodo();
  const { data, isLoading, error } = useIndirectos(periodo);
  const refContenido = useRef<HTMLDivElement>(null);

  const etiquetaPeriodo = formatearPeriodo(periodo);
  const ancla = mesAnclaPeriodo(periodo);

  const sortLote = useSortColumn<FilaLote>(null);
  const lotesOrdenados = useMemo(
    () => sortLote.sortData(data?.porLote ?? []),
    [data?.porLote, sortLote.sortData],
  );

  // Datos para el chart de evolución mensual
  const datosEvolucion = useMemo(() => {
    if (!data) return [];
    return data.evolucionMensual.map((f) => ({
      periodo: `${MESES_CORTOS[f.mes - 1]} ${String(f.anio).slice(2)}`,
      indirecto: f.indirecto_total,
      porcentaje: f.porcentaje_indirecto,
    }));
  }, [data]);

  // Delta vs período anterior (igual a Ingresos)
  const delta = useMemo(() => {
    if (!data) return null;
    const actual = data.total.indirecto_total;
    const anterior = data.totalAnterior.indirecto_total;
    if (anterior === 0) return null;
    return ((actual - anterior) / anterior) * 100;
  }, [data]);

  return (
    <div ref={refContenido} className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Indirectos</h1>
          <p className="mt-1 text-sm text-muted-foreground">{etiquetaPeriodo}</p>
        </div>
        <div data-pdf-ignore className="flex flex-wrap items-start gap-3">
          <SelectorPeriodo
            periodo={periodo}
            onSetModo={setModo}
            onSetAnio={setAnio}
            onSetMes={setMes}
            onSetTrimestre={setTrimestre}
            onSetDesde={setDesde}
            onSetHasta={setHasta}
          />
          <BotonExportar
            titulo="Indirectos"
            subtitulo={etiquetaPeriodo}
            refContenido={refContenido}
            disabled={!data}
            tablas={
              data
                ? [
                    {
                      nombre: 'Por lote',
                      encabezados: ['Lote', 'Indirecto (₡)', 'Movimientos'],
                      filas: data.porLote.map((f) => [
                        f.lote,
                        f.indirecto_total,
                        f.cantidad_movimientos,
                      ]),
                    },
                    {
                      nombre: 'Evolución mensual',
                      encabezados: ['Año', 'Mes', 'Indirecto (₡)', '% Indirecto', 'Movimientos'],
                      filas: data.evolucionMensual.map((f) => [
                        f.anio,
                        f.mes,
                        f.indirecto_total,
                        f.porcentaje_indirecto ?? '',
                        f.cantidad_movimientos,
                      ]),
                    },
                  ]
                : []
            }
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando…
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-destructive">{error.message}</CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <KpiCard
              titulo="Indirecto Total"
              monto={data.total.indirecto_total}
              destacado
              subtitulo={
                delta != null
                  ? `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}% vs anterior`
                  : undefined
              }
            />
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  % Indirecto Promedio
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-hidden px-4 pb-4">
                <span className="block truncate text-xl font-semibold">
                  {formatearPct(data.total.porcentaje_indirecto)}
                </span>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  Movimientos
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-hidden px-4 pb-4">
                <span className="block truncate text-xl font-semibold">
                  {data.total.cantidad_movimientos.toLocaleString('es-CR')}
                </span>
              </CardContent>
            </Card>
            <KpiCard titulo="Período Anterior" monto={data.totalAnterior.indirecto_total} />
          </div>

          {/* Evolución mensual */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Evolución mensual (24 meses)</CardTitle>
            </CardHeader>
            <CardContent>
              {datosEvolucion.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart
                    data={datosEvolucion}
                    margin={{ left: 8, right: 16, top: 8, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis
                      dataKey="periodo"
                      tick={{ fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      interval={2}
                    />
                    <YAxis
                      tickFormatter={abreviarCRC}
                      tick={{ fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(v) => [abreviarCRC(Number(v)), 'Indirecto']}
                      cursor={{ fill: 'hsl(var(--muted))' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="indirecto"
                      stroke="#7c3aed"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">Sin datos</p>
              )}
            </CardContent>
          </Card>

          {/* Por lote */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Indirecto por lote</CardTitle>
            </CardHeader>
            <CardContent>
              {(data.porLote.length ?? 0) > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(200, data.porLote.length * 24)}>
                  <BarChart
                    data={data.porLote.slice(0, 20)}
                    layout="vertical"
                    margin={{ left: 8, right: 32, top: 4, bottom: 4 }}
                  >
                    <XAxis
                      type="number"
                      tickFormatter={abreviarCRC}
                      tick={{ fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="lote"
                      width={70}
                      tick={{ fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(v) => [abreviarCRC(Number(v)), 'Indirecto']}
                      cursor={{ fill: 'hsl(var(--muted))' }}
                    />
                    <Bar dataKey="indirecto_total" radius={[0, 4, 4, 0]}>
                      {data.porLote.slice(0, 20).map((d) => (
                        <Cell key={d.lote} fill="#7c3aed" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">Sin datos</p>
              )}
            </CardContent>
          </Card>

          {/* Tabla por lote */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Detalle por lote</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-96 overflow-y-auto px-6 pb-4">
                <Table>
                  <TableHeader className="sticky top-0 bg-card">
                    <TableRow>
                      <TableHead>
                        <SortableHeader
                          label="Lote"
                          sortKey="lote"
                          activeSortKey={sortLote.sortKey}
                          direction={sortLote.direction}
                          onSort={sortLote.handleSort}
                        />
                      </TableHead>
                      <TableHead className="text-right">
                        <SortableHeader
                          label="Indirecto"
                          sortKey="indirecto_total"
                          activeSortKey={sortLote.sortKey}
                          direction={sortLote.direction}
                          onSort={sortLote.handleSort}
                          align="right"
                        />
                      </TableHead>
                      <TableHead className="text-right">
                        <SortableHeader
                          label="Movimientos"
                          sortKey="cantidad_movimientos"
                          activeSortKey={sortLote.sortKey}
                          direction={sortLote.direction}
                          onSort={sortLote.handleSort}
                          align="right"
                        />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lotesOrdenados.map((f) => (
                      <TableRow key={f.lote}>
                        <TableCell>{f.lote}</TableCell>
                        <TableCell className="text-right">
                          <MontoCRC monto={f.indirecto_total} />
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {f.cantidad_movimientos.toLocaleString('es-CR')}
                        </TableCell>
                      </TableRow>
                    ))}
                    {lotesOrdenados.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground">
                          Sin datos por lote
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <ComentarioSeccion anio={ancla.anio} mes={ancla.mes} scope="seccion" seccionId="indirectos" />
    </div>
  );
}
